/**
 * Joining per-area packages into the one project package `AEFI` opens.
 *
 * The API builds a package from one job, which is one page and one polygon. A project is many
 * of those, so each area is packaged on its own - which keeps the server-side review and scale
 * checks per result - and this combines the finished pages. Pure, so the shape the connector
 * receives is asserted here rather than by opening AutoCAD.
 */

import type { ConversionPackage, PackagePage } from "./package.ts";

/** One finished area: the single-page package the API returned, and where it sits in the project. */
export interface AreaPackage {
  readonly package: ConversionPackage;
  readonly pageNumber: number;
  readonly areaIndex: number;
  /** Set when the page has no scale of its own and borrows this page's. */
  readonly inheritsCalibrationFromPage: number | null;
  readonly skipKeyplan: boolean;
}

/** Project-level facts only the browser knows; the per-area packages from the API carry neither. */
export interface ProjectExtras {
  /** USPS code. Names the 03 drawing. Left off the package when empty. */
  readonly state?: string;
  readonly country?: string;
}

export class ProjectPackageError extends Error {}

export function buildProjectPackage(
  areas: readonly AreaPackage[],
  extras: ProjectExtras = {},
): ConversionPackage {
  const first = areas[0];
  if (first === undefined) throw new ProjectPackageError("A project package needs at least one area.");

  // A page can lend its scale only if it is itself in the package: the connector resolves
  // inheritance through that page's own entry. A page that only carries the scale bar has no
  // areas, so an area borrowing from it keeps the calibration the API built from the owner's
  // points, which is the same scale.
  const owners = new Set(areas.filter((a) => a.inheritsCalibrationFromPage === null).map((a) => a.pageNumber));

  const seen = new Set<string>();
  const pages: PackagePage[] = [];
  for (const area of areas) {
    const key = `${area.pageNumber}:${area.areaIndex}`;
    if (seen.has(key)) {
      throw new ProjectPackageError(`Page ${area.pageNumber} has two areas numbered ${area.areaIndex + 1}.`);
    }
    seen.add(key);

    const source = area.package.pages[0];
    if (area.package.pages.length !== 1 || source === undefined) {
      throw new ProjectPackageError("Each area must be packaged as exactly one page.");
    }
    const { calibration: own, ...rest } = source;
    const inherits =
      area.inheritsCalibrationFromPage !== null && owners.has(area.inheritsCalibrationFromPage)
        ? area.inheritsCalibrationFromPage
        : null;
    pages.push({
      ...rest,
      pageNumber: area.pageNumber,
      areaIndex: area.areaIndex,
      ...(inherits === null
        ? own === undefined ? {} : { calibration: own }
        : { inheritsCalibrationFromPage: inherits }),
      ...(area.skipKeyplan ? { skipKeyplan: true } : {}),
    });
  }

  pages.sort((a, b) => a.pageNumber - b.pageNumber || a.areaIndex - b.areaIndex);

  const calibrated = pages.find((p) => p.calibration !== undefined);
  if (calibrated === undefined) throw new ProjectPackageError("No page in the project has a scale.");

  const reviewed = areas
    .map((a) => a.package)
    .filter((p) => p.reviewedBy !== "" && p.reviewedAtUtc !== undefined);
  const latest = reviewed
    .map((p) => p.reviewedAtUtc as string)
    .sort()
    .at(-1);
  const reviewer = reviewed[0]?.reviewedBy ?? "";

  return {
    ...first.package,
    defaultCalibrationSourcePage: calibrated.pageNumber,
    ...(extras.state === undefined || extras.state === "" ? {} : { state: extras.state }),
    ...(extras.country === undefined ? {} : { country: extras.country }),
    pages,
    reviewedBy: reviewer,
    ...(latest === undefined ? {} : { reviewedAtUtc: latest }),
  };
}
